/**
 * The job card's read-only "Assets" list (SafeShare slice 4.1). Admin only:
 * the job's SafeShare assets (one per rig capture of a client job, one per
 * handheld attempt) with their serial number, display name, client alias and
 * current variants, read through the listJobAssets callable (Zelma's
 * projection: no paths, no links). Nothing here writes.
 */
import React, { useEffect, useState } from "react";
import { callable } from "../../../../firebase.js";
import { C, lbl } from "../../../../lib/styles.js";

export const assetSourceLabel = (a) =>
  a.source === "handheld" ? `Handheld${a.attempt ? `, attempt ${a.attempt}` : ""}` : "Rig capture";

export default function AssetsList({ jobId, load }) {
  const [state, setState] = useState({ loading: true, error: null, assets: [] });

  useEffect(() => {
    let live = true;
    const fetchAssets = load || (async (id) => (await callable("listJobAssets", { timeout: 20000 })({ jobId: id })).data);
    setState({ loading: true, error: null, assets: [] });
    fetchAssets(jobId)
      .then((r) => { if (live) setState({ loading: false, error: null, assets: r?.assets || [] }); })
      .catch((e) => { if (live) setState({ loading: false, error: e?.message || "Could not load the assets.", assets: [] }); });
    return () => { live = false; };
  }, [jobId, load]);

  return <AssetsTable state={state} />;
}

/** The list itself, from {loading, error, assets}: pure, so it renders on the server for tests. */
export function AssetsTable({ state }) {
  return (
    <div style={{ marginTop: 20 }} data-testid="job-assets">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <label style={{ ...lbl, marginBottom: 0 }}>Assets</label>
        {!state.loading && !state.error && <span style={{ fontSize: 10, color: C.muted }}>{state.assets.length}</span>}
      </div>
      {state.loading ? (
        <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>
      ) : state.error ? (
        <div style={{ fontSize: 12, color: C.red || "#c0392b" }}>{state.error}</div>
      ) : state.assets.length === 0 ? (
        <div style={{ fontSize: 12, color: C.muted }}>No SafeShare assets for this job (only a client job's captures become assets).</div>
      ) : (
        <div className="jc-cardtable" style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>Serial number</th>
                <th style={{ padding: "6px 8px" }}>Name</th>
                <th style={{ padding: "6px 8px" }}>Client alias</th>
                <th style={{ padding: "6px 8px" }}>From</th>
                <th style={{ padding: "6px 8px" }}>Deliverable</th>
                <th style={{ padding: "6px 8px" }}>Versions</th>
              </tr>
            </thead>
            <tbody>
              {state.assets.map((a) => (
                <tr key={a.sn} style={{ borderTop: `1px solid ${C.border}`, color: C.text }}>
                  <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>{a.sn}</td>
                  <td style={{ padding: "6px 8px" }}>
                    {a.displayName}
                    {a.pendingDeletion && <span style={{ color: C.muted }}> (deletion pending)</span>}
                  </td>
                  <td style={{ padding: "6px 8px" }}>{a.alias || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{assetSourceLabel(a)}</td>
                  <td style={{ padding: "6px 8px" }}>
                    {a.presets?.length ? `${a.presets.map((p) => p.name).join(", ")}${a.presetsFrom === "job" ? " (job default)" : ""}` : "—"}
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    {[...a.variants, ...(a.sharedVersions ? [`shared project${a.sharedVersions > 1 ? ` ×${a.sharedVersions}` : ""}`] : []), ...(a.coming?.length ? [`${a.coming.join(", ")}: coming`] : [])].join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
